<script setup lang="ts">
import { ref } from "vue"
import { EyeIcon, EyeOffIcon } from "@heroicons/vue/outline"
import { vMaska } from "maska"

interface Props {
  id: string
  modelValue?: string | number
  maxLength?: number
  autocomplete?: string
  type?: string
  placeholder?: string
  isLoading?: boolean
  disabled?: boolean
  name?: string
  mask?: string
  feedback?: string
  readonly?: boolean
  inputHeight?: string
  inputPaddingClass?: string
}
const props = withDefaults(defineProps<Props>(), {
  maxLength: 0,
  autocomplete: "",
  type: "text",
  placeholder: "",
  isLoading: false,
  disabled: false,
  name: "",
  feedback: "",
  inputHeight: undefined,
  inputPaddingClass: undefined
})

const emit = defineEmits(["update:modelValue"])
const updateValue = (value: string | number) => {
  emit("update:modelValue", value)
}

const increment = (qty: 1 | -1) => {
  updateValue(Number(props.modelValue) + qty)
}

const showPassword = ref(false)
const toggleShowPassword = () => {
  showPassword.value = !showPassword.value
}
defineExpose({ toggleShowPassword })
</script>

<template>
  <div class="relative container-input">
    <div
      :class="feedback ? 'text-error' : 'text-gray-400'"
      class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none container-icon-left"
    >
      <slot name="icon-left" />
    </div>

    <div
      class="absolute inset-y-0 right-0 flex items-center px-2 container-icon-right"
    >
      <span
        v-if="type === 'password'"
        id="icons_wrapper"
        @click="toggleShowPassword()"
      >
        <div v-if="showPassword" data-test="PasswordVisibleIcon">
          <EyeOffIcon class="w-5 h-5 text-gray-400" />
        </div>
        <div v-else data-test="PasswordInvisibleIcon">
          <EyeIcon class="w-5 h-5 text-gray-400" />
        </div>
      </span>

      <span v-else-if="type === 'number'">
        <div>
          <span class="cursor-pointer" @click="increment(1)">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-3 h-3 text-gray-400"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M4.5 15.75l7.5-7.5 7.5 7.5"
              />
            </svg>
          </span>
        </div>
        <div>
          <span class="cursor-pointer" @click="increment(-1)">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="w-3 h-3 text-gray-400"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 8.25l-7.5 7.5-7.5-7.5"
              />
            </svg>
          </span>
        </div>
      </span>

      <div v-else class="text-gray-400">
        <slot name="icon-right" />
      </div>
    </div>
    <input
      :id="id"
      v-maska
      :name="name"
      :value="modelValue"
      :type="showPassword ? 'text' : type"
      :placeholder="placeholder"
      :autocomplete="autocomplete"
      :readonly="isLoading || readonly"
      :disabled="disabled"
      :maxlength="maxLength > 0 ? maxLength : ''"
      :class="[
        (isLoading || disabled) && 'opacity-50 cursor-not-allowed',
        feedback
          ? 'border-error focus:border-error'
          : 'border-gray-300 dark:border-gray-700 focus:border-gray-300 focus:dark:border-gray-700',
        inputHeight,
        inputPaddingClass ? inputPaddingClass : 'px-10'
      ]"
      :data-maska="mask"
      class="block w-full py-2 text-sm border rounded-md shadow-sm appearance-none placeholder-placeholderText input dark:bg-gray-700 focus:outline-1 focus:outline-gray-200 focus:dark:outline-gray-700 focus:ring-0 focus:ring-transparent"
      @input="(e) => updateValue((e.target as HTMLInputElement).value)"
      @keydown="
        e => {
          if (e.code === 'Space') {
            e.stopPropagation()
          }
        }
      "
    />
  </div>
</template>

<style scoped lang="scss">
.container-icon-left:empty ~ input {
  @apply pl-2;
}

.container-icon-right:empty {
  display: none;
}

.container-icon-right:empty ~ .input {
  @apply pr-2;
}

input[type="number"]::-webkit-inner-spin-button {
  opacity: 0;
}
</style>
